import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard, List, LogOut, Plus, TrendingUp, Clock,
  CheckCircle, Building2, Users, BarChart2, ArrowUpRight,
  ArrowDownRight, DollarSign, Activity, ArrowLeft
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, PieChart, Pie, Cell, AreaChart, Area
} from 'recharts';
import { publicaciones } from '../data/mockPublicaciones';
import '../demo-professional.css';

// — Datos para gráficas —
const weeklyActivity = [
  { dia: 'Lun', ofertas: 18, publicaciones: 2 },
  { dia: 'Mar', ofertas: 27, publicaciones: 3 },
  { dia: 'Mié', ofertas: 14, publicaciones: 1 },
  { dia: 'Jue', ofertas: 35, publicaciones: 4 },
  { dia: 'Vie', ofertas: 22, publicaciones: 2 },
  { dia: 'Sáb', ofertas: 9,  publicaciones: 1 },
  { dia: 'Dom', ofertas: 5,  publicaciones: 0 },
];

const areaData = [
  { mes: 'Ago', total: 45 },
  { mes: 'Sep', total: 62 },
  { mes: 'Oct', total: 58 },
  { mes: 'Nov', total: 81 },
  { mes: 'Dic', total: 97 },
  { mes: 'Ene', total: 115 },
];

const PIE_COLORS = ['#f97316', '#64748b', '#22c55e'];

// — Tooltip personalizado —
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: '10px 14px' }}>
        <p style={{ color: '#94a3b8', fontSize: 12, marginBottom: 4 }}>{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color, fontSize: 13, fontWeight: 700, margin: 0 }}>
            {p.name}: {p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// — Shared Demo Nav —
function DemoNav({ active }) {
  const navigate = useNavigate();
  const handleLogout = () => {
    localStorage.removeItem('demo_authenticated');
    navigate('/propuesta/demo');
  };

  const tabs = [
    { id: 'dashboard',   label: 'Dashboard',    icon: <LayoutDashboard size={15} />, path: '/propuesta/demo/dashboard' },
    { id: 'publicaciones', label: 'Publicaciones', icon: <List size={15} />, path: '/propuesta/demo/publicaciones' },
    { id: 'revendedores',  label: 'Revendedores',  icon: <Users size={15} />, path: '/propuesta/demo/revendedores' },
    { id: 'reportes',      label: 'Reportes',      icon: <BarChart2 size={15} />, path: '/propuesta/demo/reportes' },
  ];

  return (
    <>
      <header className="demo-header sticky top-0 z-20">
        <div className="px-4 sm:px-6 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}>
              <Building2 className="text-white" size={18} />
            </div>
            <div>
              <h1 className="text-base font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>Sistema de Tasación</h1>
              <p className="text-xs" style={{ color: '#94a3b8' }}>Vladimir & Aerocar</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/propuesta')} className="flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition hover:bg-white/5" style={{ color: '#94a3b8' }} data-testid="demo-back-to-propuesta" title="Volver a la propuesta">
              <ArrowLeft size={15} />
              <span className="hidden sm:inline">Propuesta</span>
            </button>
            <button onClick={handleLogout} className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition" style={{ color: '#94a3b8' }}>
              <LogOut size={15} />
              <span className="hidden sm:inline">Salir</span>
            </button>
          </div>
        </div>
      </header>

      <nav style={{ background: 'white', borderBottom: '1px solid #e2e8f0' }}>
        <div className="px-4 sm:px-6 py-2 flex items-center gap-1 sm:gap-2 overflow-x-auto">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => navigate(tab.path)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg whitespace-nowrap transition ${
                active === tab.id
                  ? 'text-orange-600 bg-orange-50'
                  : 'text-gray-500 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              {tab.icon}
              <span>{tab.label}</span>
            </button>
          ))}
          <button
            onClick={() => navigate('/propuesta/demo/nueva')}
            className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white rounded-lg ml-auto whitespace-nowrap transition"
            style={{ background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)' }}
          >
            <Plus size={15} />
            <span>Nueva</span>
          </button>
        </div>
      </nav>
    </>
  );
}

export default function DemoDashboard() {
  const [animated, setAnimated] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setAnimated(true), 100);
    return () => clearTimeout(t);
  }, []);

  // — Métricas calculadas —
  const totalPublicaciones = publicaciones.length;
  const enProceso = publicaciones.filter(p => !p.mercadoLibre || !p.gerencia).length;
  const completadas = publicaciones.filter(p => p.mercadoLibre && p.gerencia && p.revendedores.length > 0).length;
  const totalOfertas = publicaciones.reduce((s, p) => s + p.revendedores.length, 0);
  const mejorOferta = Math.max(...publicaciones.flatMap(p => p.revendedores.map(r => r.oferta)));
  const revendedoresActivos = new Set(publicaciones.flatMap(p => p.revendedores.map(r => r.nombre))).size;

  const pieData = [
    { name: 'En proceso', value: enProceso },
    { name: 'Sin ofertas', value: publicaciones.filter(p => p.revendedores.length === 0).length },
    { name: 'Completas',   value: completadas },
  ];

  const topVehiculos = publicaciones
    .sort((a, b) => b.revendedores.length - a.revendedores.length)
    .slice(0, 6)
    .map(p => ({
      name: `${p.marca} ${p.modelo}`,
      ofertas: p.revendedores.length,
    }));

  const kpis = [
    {
      label: 'Publicaciones',
      value: totalPublicaciones,
      sub: '+3 esta semana',
      up: true,
      icon: <List size={18} />,
      color: '#3b82f6',
      bg: '#eff6ff',
    },
    {
      label: 'Ofertas Recibidas',
      value: totalOfertas,
      sub: '+12 hoy',
      up: true,
      icon: <Activity size={18} />,
      color: '#f97316',
      bg: '#fff7ed',
    },
    {
      label: 'Mejor Oferta',
      value: `$${(mejorOferta / 1000).toFixed(1)}k`,
      sub: 'Nissan Kicks',
      up: true,
      icon: <DollarSign size={18} />,
      color: '#22c55e',
      bg: '#f0fdf4',
    },
    {
      label: 'Revendedores',
      value: revendedoresActivos,
      sub: '10 activos',
      up: false,
      icon: <Users size={18} />,
      color: '#8b5cf6',
      bg: '#faf5ff',
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: '#f1f5f9' }}>
      <DemoNav active="dashboard" />

      <main className="px-4 sm:px-6 py-6 max-w-7xl mx-auto">

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {kpis.map((k, i) => (
            <div
              key={i}
              className="demo-card p-5 transition-all"
              style={{
                opacity: animated ? 1 : 0,
                transform: animated ? 'translateY(0)' : 'translateY(12px)',
                transition: `opacity 0.4s ease ${i * 0.08}s, transform 0.4s ease ${i * 0.08}s`,
              }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: k.bg, color: k.color }}>
                  {k.icon}
                </div>
                <span className={`flex items-center gap-0.5 text-xs font-semibold ${k.up ? 'text-green-600' : 'text-red-500'}`}>
                  {k.up ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  {k.up ? '+' : ''}
                </span>
              </div>
              <div className="text-2xl font-bold mb-0.5" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>
                {animated ? k.value : '—'}
              </div>
              <div className="text-xs font-medium" style={{ color: '#64748b' }}>{k.label}</div>
              <div className="text-xs mt-1" style={{ color: '#94a3b8' }}>{k.sub}</div>
            </div>
          ))}
        </div>

        {/* Charts Row */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">

          {/* Bar Chart — Actividad Semanal */}
          <div className="lg:col-span-2 demo-card p-5">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>Actividad Semanal</h2>
                <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>Ofertas y publicaciones por día</p>
              </div>
              <span className="text-xs px-2.5 py-1 rounded-full font-semibold" style={{ background: '#fff7ed', color: '#ea580c' }}>Esta semana</span>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={weeklyActivity} margin={{ top: 0, right: 0, left: -20, bottom: 0 }} barCategoryGap="35%">
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="dia" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(249,115,22,0.05)' }} />
                <Bar dataKey="ofertas" name="Ofertas" fill="#f97316" radius={[4, 4, 0, 0]} />
                <Bar dataKey="publicaciones" name="Publicaciones" fill="#dbeafe" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="flex items-center gap-4 mt-3">
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ background: '#f97316' }} /><span className="text-xs" style={{ color: '#64748b' }}>Ofertas</span></div>
              <div className="flex items-center gap-1.5"><div className="w-3 h-3 rounded-sm" style={{ background: '#dbeafe' }} /><span className="text-xs" style={{ color: '#64748b' }}>Publicaciones</span></div>
            </div>
          </div>

          {/* Pie Chart — Estado */}
          <div className="demo-card p-5">
            <div className="mb-5">
              <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>Estado de Tasaciones</h2>
              <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>Distribución actual</p>
            </div>
            <div className="flex items-center justify-center">
              <PieChart width={160} height={160}>
                <Pie data={pieData} cx={75} cy={75} innerRadius={45} outerRadius={70} dataKey="value" paddingAngle={3}>
                  {pieData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={PIE_COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </div>
            <div className="space-y-2 mt-3">
              {pieData.map((d, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-2.5 h-2.5 rounded-full" style={{ background: PIE_COLORS[i] }} />
                    <span className="text-xs" style={{ color: '#64748b' }}>{d.name}</span>
                  </div>
                  <span className="text-xs font-bold" style={{ color: '#0f172a' }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Línea de tendencia */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2 demo-card p-5">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>Tendencia de Ofertas</h2>
                <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>Acumulado últimos 6 meses</p>
              </div>
              <div className="flex items-center gap-1 text-xs font-semibold text-green-600">
                <TrendingUp size={13} /> +28%
              </div>
            </div>
            <ResponsiveContainer width="100%" height={140}>
              <AreaChart data={areaData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f97316" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#f97316" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" vertical={false} />
                <XAxis dataKey="mes" tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="total" name="Ofertas" stroke="#f97316" strokeWidth={2} fill="url(#areaGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Top vehículos por ofertas */}
          <div className="demo-card p-5">
            <div className="mb-4">
              <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>Top por Ofertas</h2>
              <p className="text-xs mt-0.5" style={{ color: '#94a3b8' }}>Vehículos más demandados</p>
            </div>
            <div className="space-y-3">
              {topVehiculos.slice(0, 5).map((v, i) => (
                <div key={i} className="flex items-center gap-3">
                  <span className="text-xs font-bold w-4 text-center" style={{ color: i === 0 ? '#f97316' : '#94a3b8' }}>
                    {i + 1}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-semibold truncate" style={{ color: '#334155' }}>{v.name}</div>
                    <div className="mt-1 h-1.5 rounded-full overflow-hidden" style={{ background: '#f1f5f9' }}>
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: animated ? `${(v.ofertas / topVehiculos[0].ofertas) * 100}%` : '0%',
                          background: i === 0 ? '#f97316' : '#cbd5e1',
                          transition: `width 0.8s ease ${i * 0.1}s`,
                        }}
                      />
                    </div>
                  </div>
                  <span className="text-xs font-bold" style={{ color: '#0f172a' }}>{v.ofertas}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabla publicaciones recientes */}
        <div className="demo-card overflow-hidden">
          <div className="px-5 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid #e2e8f0' }}>
            <h2 className="text-base font-bold" style={{ fontFamily: 'Outfit, sans-serif', color: '#0f172a' }}>Publicaciones Recientes</h2>
            <button
              className="text-sm font-medium"
              style={{ color: '#f97316' }}
              onClick={() => window.location.href = '/propuesta/demo/publicaciones'}
            >
              Ver todas →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                <tr>
                  {['Vehículo', 'Patente', 'Fecha', 'Estado', 'Ofertas', ''].map((h, i) => (
                    <th key={i} className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider" style={{ color: '#64748b' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody style={{ background: 'white' }}>
                {publicaciones.slice(0, 6).map((pub, idx) => (
                  <tr key={pub.id} style={{ borderBottom: idx < 5 ? '1px solid #f8fafc' : 'none' }} className="hover:bg-gray-50 transition">
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <div className="text-sm font-semibold" style={{ color: '#0f172a' }}>{pub.marca} {pub.modelo}</div>
                      <div className="text-xs" style={{ color: '#94a3b8' }}>{pub.año} · {pub.motor}</div>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm font-mono" style={{ color: '#475569' }}>{pub.patente}</td>
                    <td className="px-5 py-3.5 whitespace-nowrap text-sm" style={{ color: '#475569' }}>
                      {new Date(pub.fecha).toLocaleDateString('es-UY')}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      {pub.mercadoLibre && pub.gerencia ? (
                        <span className="demo-badge-success inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium">
                          <CheckCircle size={11} /> Completa
                        </span>
                      ) : (
                        <span className="demo-badge-warning inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium">
                          <Clock size={11} /> En proceso
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <span className="text-sm font-bold" style={{ color: pub.revendedores.length > 5 ? '#f97316' : '#0f172a' }}>
                        {pub.revendedores.length}
                      </span>
                    </td>
                    <td className="px-5 py-3.5 whitespace-nowrap">
                      <a href={`/propuesta/demo/publicacion/${pub.id}`} className="text-sm font-medium" style={{ color: '#3b82f6' }}>
                        Ver →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
}
